export interface MDTReportTemplateData {
  reportId: number;
  patientName: string;
  genDateStr: string;
  reportStatus: string;
  ageSexStr: string;
  dxStr: string;
  subtypeStr: string;
  stageStr: string;
  riskCategory: string;
  ecogStatus: string;
  ki67: string;
  myc: string;
  recommendedRegimen: string;
  cyclesStr: string;
  rationaleText: string;
  defaultRegimenMatrix: { regimen: string; status: string; reason: string }[];
  discussionItems: { label: string; desc: string }[];
  evidencePoints: string[];
  supportiveRows: { domain: string; status: string; isSuccess: boolean; guidance: string }[];
  hospital?: { hospitalName?: string; hospitalCode?: string } | null;
}

export const generateMedicalReportHTML = (data: MDTReportTemplateData) => {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>MDT Clinical Report #${data.reportId} - ${data.patientName}</title>
  <style>
    @page { size: A4; margin: 15mm; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
      color: #0f172a;
      background: #fff;
      margin: 0;
      padding: 32px;
      font-size: 13px;
      line-height: 1.5;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 3px solid #0f172a;
      padding-bottom: 12px;
      margin-bottom: 20px;
    }
    .header-title h1 {
      font-size: 20px;
      font-weight: 800;
      color: #0f172a;
      margin: 0 0 4px 0;
      text-transform: uppercase;
      letter-spacing: -0.5px;
    }
    .header-title .subtitle {
      font-size: 11px;
      color: #64748b;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .hospital-line {
      font-size: 11px;
      color: #0284c7;
      font-weight: 700;
      margin-top: 4px;
    }
    .header-meta {
      text-align: right;
      font-size: 11px;
      color: #475569;
    }
    .status-badge {
      display: inline-block;
      padding: 3px 8px;
      background: #e2e8f0;
      color: #0f172a;
      font-weight: 700;
      font-size: 10px;
      border-radius: 4px;
      text-transform: uppercase;
      margin-top: 4px;
    }
    .patient-banner {
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-left: 4px solid #0284c7;
      border-radius: 6px;
      padding: 12px 16px;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
      margin-bottom: 20px;
    }
    .patient-field label {
      display: block;
      font-size: 9px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      margin-bottom: 2px;
    }
    .patient-field strong {
      font-size: 12px;
      color: #0f172a;
    }
    .section {
      margin-bottom: 20px;
      page-break-inside: avoid;
    }
    .section-title {
      font-size: 11px;
      font-weight: 800;
      color: #0f172a;
      background: #f1f5f9;
      padding: 6px 10px;
      border-left: 3px solid #0f172a;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 10px;
    }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin-bottom: 12px;
    }
    .fact-box {
      background: #fff;
      border: 1px solid #e2e8f0;
      padding: 8px 10px;
      border-radius: 4px;
    }
    .fact-box label {
      font-size: 9px;
      color: #64748b;
      font-weight: 700;
      display: block;
      text-transform: uppercase;
    }
    .fact-box strong {
      font-size: 12px;
      color: #0f172a;
    }
    .narrative {
      font-size: 12px;
      color: #334155;
      line-height: 1.6;
      background: #fafafa;
      padding: 10px 12px;
      border: 1px solid #f1f5f9;
      border-radius: 4px;
    }
    .rec-box {
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-left: 4px solid #16a34a;
      padding: 12px 16px;
      border-radius: 6px;
      margin-bottom: 12px;
    }
    .rec-box .regimen {
      font-size: 18px;
      font-weight: 800;
      color: #14532d;
      font-family: monospace;
    }
    .rec-box .cycle {
      font-size: 11px;
      color: #166534;
      font-weight: 600;
      margin-top: 2px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
      margin-top: 8px;
    }
    th, td {
      border: 1px solid #cbd5e1;
      padding: 6px 10px;
      text-align: left;
    }
    th {
      background: #f8fafc;
      font-weight: 700;
      color: #475569;
      text-transform: uppercase;
      font-size: 9px;
    }
    .badge-selected { color: #15803d; font-weight: 800; }
    .badge-alternative { color: #1d4ed8; font-weight: 700; }
    .badge-rejected { color: #b91c1c; font-weight: 600; }
    .discussion-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }
    .disc-item {
      border: 1px solid #e2e8f0;
      padding: 8px 10px;
      border-radius: 4px;
    }
    .disc-item strong { display: block; font-size: 11px; color: #0f172a; margin-bottom: 2px; }
    .disc-item span { font-size: 11px; color: #475569; }
    ul.evidence { margin: 0; padding-left: 18px; color: #334155; font-size: 11.5px; }
    ul.evidence li { margin-bottom: 4px; }
    .disclaimer {
      margin-top: 30px;
      border-top: 1px solid #e2e8f0;
      padding-top: 12px;
      font-size: 10px;
      color: #94a3b8;
      text-align: center;
      font-style: italic;
    }
    .signature-block {
      margin-top: 24px;
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #475569;
      border-top: 1px dashed #cbd5e1;
      padding-top: 16px;
    }
    .sig-line { width: 200px; border-bottom: 1px solid #0f172a; margin-top: 20px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-title">
      <h1>Multidisciplinary Team Clinical Report</h1>
      ${data.hospital?.hospitalName ? `<div class="hospital-line">${data.hospital.hospitalName}${data.hospital.hospitalCode ? ` (${data.hospital.hospitalCode})` : ''}</div>` : ''}
    </div>
    <div class="header-meta">
      <div>Report ID: <strong>#${data.reportId}</strong></div>
      <div>Date: <strong>${data.genDateStr}</strong></div>
      <div class="status-badge">STATUS: ${(data.reportStatus || 'DRAFT').toUpperCase()}</div>
    </div>
  </div>

  <div class="patient-banner">
    <div class="patient-field">
      <label>Patient Name</label>
      <strong>${data.patientName}</strong>
    </div>
    <div class="patient-field">
      <label>Age / Sex</label>
      <strong>${data.ageSexStr}</strong>
    </div>
    <div class="patient-field">
      <label>Primary Diagnosis</label>
      <strong>${data.dxStr}</strong>
    </div>
    <div class="patient-field">
      <label>Subtype & Stage</label>
      <strong>${data.subtypeStr} (${data.stageStr})</strong>
    </div>
  </div>

  <div class="section">
    <div class="section-title">1. Case Summary</div>
    <div class="summary-grid">
      <div class="fact-box"><label>Diagnosis</label><strong>${data.subtypeStr}</strong></div>
      <div class="fact-box"><label>Stage</label><strong>${data.stageStr}</strong></div>
      <div class="fact-box"><label>Risk Category</label><strong>${data.riskCategory}</strong></div>
      <div class="fact-box"><label>ECOG Status</label><strong>${data.ecogStatus}</strong></div>
      <div class="fact-box"><label>Ki-67 Index</label><strong>${data.ki67}</strong></div>
      <div class="fact-box"><label>MYC Expression</label><strong>${data.myc}</strong></div>
    </div>
    <div class="narrative">
      This is a 72-year-old male with relapsed/refractory diffuse large B-cell lymphoma (DLBCL) of the Activated B-Cell (ABC)/non-Germinal Center B-Cell (non-GCB) subtype, presenting with progressive fatigue, drenching night sweats, reduced appetite, and increasing abdominal discomfort. He has a history of previous R-CHOP treatment with subsequent disease progression, ischaemic heart disease with previous coronary stent, chronic kidney disease stage 2, and hypertension.
    </div>
  </div>

  <div class="section">
    <div class="section-title">2. Treatment Recommendation & Options</div>
    <div class="rec-box">
      <div style="font-size: 10px; font-weight: 700; color: #15803d; text-transform: uppercase;">Primary Recommendation</div>
      <div class="regimen">${data.recommendedRegimen}</div>
      <div class="cycle">${data.cyclesStr}</div>
    </div>
    <div style="font-size: 11px; margin-bottom: 8px;"><strong>Rationale:</strong> ${data.rationaleText}</div>

    <table>
      <thead>
        <tr>
          <th>Regimen</th>
          <th>Decision Status</th>
          <th>Clinical Rationale</th>
        </tr>
      </thead>
      <tbody>
        ${data.defaultRegimenMatrix.map(r => `
          <tr>
            <td><strong>${r.regimen}</strong></td>
            <td class="badge-${r.status.toLowerCase()}">${r.status}</td>
            <td>${r.reason}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">3. MDT Discussion Points</div>
    <div class="discussion-grid">
      ${data.discussionItems.map(d => `
        <div class="disc-item">
          <strong>${d.label}</strong>
          <span>${d.desc}</span>
        </div>
      `).join('')}
    </div>
  </div>

  <div class="section">
    <div class="section-title">4. Evidence & Trial Benchmarks</div>
    <ul class="evidence">
      ${data.evidencePoints.map(e => `<li>${e}</li>`).join('')}
    </ul>
  </div>

  <div class="section">
    <div class="section-title">5. Supportive Care & Plan</div>
    <table>
      <thead>
        <tr>
          <th>Domain</th>
          <th>Status</th>
          <th>Guidance</th>
        </tr>
      </thead>
      <tbody>
        ${data.supportiveRows.map(s => `
          <tr>
            <td><strong>${s.domain}</strong></td>
            <td style="color: ${s.isSuccess ? '#15803d' : '#b45309'}; font-weight: 700;">${s.status}</td>
            <td>${s.guidance}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  </div>

  <div class="signature-block">
    <div>
      <div>Lead Oncologist Signature</div>
      <div class="sig-line"></div>
    </div>
    <div>
      <div>MDT Coordinator Verification</div>
      <div class="sig-line"></div>
    </div>
  </div>

  <div class="disclaimer">
    CONFIDENTIAL MEDICAL DOCUMENT — MULTI-DISCIPLINARY TEAM AI-ASSISTED CLINICAL DECISION SUPPORT.<br>
    Requires signature and validation by treating clinical oncologist before implementation.
  </div>
</body>
</html>`;
};
